/**
 * PaymentSystem entity class
 */

/**
 * PaymentSystemFactory
 * returns PaymentSystem entity class
 *
 * @class PaymentSystemFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.PaymentSystem} class
 *
 * @ngdoc factory
 * @name mwApp.factory:PaymentSystemFactory
 */
/* @ngInject */
export default function PaymentSystemFactory ($http, alertLiteSvc, Entity, m2mMixin, onOffMixin) {
  /* === PaymentSystem class === */

  /**
   * PaymentSystem model
   *
   * @class PaymentSystem
   * @classdesc PaymentSystem model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @return {models.PaymentSystem} PaymentSystem entity
   */
  function PaymentSystem () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  PaymentSystem.prototype = Object.create(Entity.prototype)
  PaymentSystem.prototype.constructor = PaymentSystem

  /* === PaymentSystem instance methods & fields === */

  Object.assign(PaymentSystem.prototype, {

    /**
     * instance fields
     *
     * @memberof models.PaymentSystem#
     * @type {Object}
     */
    _fields: {
      id: Number,
      autoPay: false,
      countries: [{
        id: Number,
        name: String,
        isoCode: String
      }],
      currencies: ['Currency'],
      defaultPaymentSystem: false,
      defaultTransactionFeePercent: 0,
      disableOtherPaymentMethods: false,
      enableTransactionFee: false,
      enabled: false,
      live: false,
      login: String,
      network: null, // do not convert to `Network` entity
      merchantId: String,
      hashKey: String,
      hashIv: String,
      ezPayMerchantId: String,
      ezPayHashKey: String,
      ezPayHashIv: String,
      ezPayDonationCode: Number,
      password: String,
      password2: String,
      paymentMethods: [{
        paymentMethod: String,
        transactionFeePercent: Number
      }],
      paymentSystemType: String,
      privateKey: String,
      useSystemDefault: Boolean,
      locationEnabled: Boolean,
      locationRequired: Boolean,
      locations: ['Location'],
      externalPaymentSystems: ['ExternalPaymentSystem'],
      publicClientKey: String,
      publicKey: String,
      redirectUri: String,
      //  for TRUE_ONLINE
      mcsUrl: String,
      mcsApiKey: String,
      dcbUrl: String,
      dcbApiKey: String,
      vatPercent: Number,
      webhookSettings: {
        enabled: Boolean,
        secret: String,
        webhookId: String,
      }
    },

    /**
     * url template
     *
     * @memberof models.PaymentSystem#
     * @type {String}
     */
    _urlTemplate: 'api/paymentsystems/{{::id}}',

    /**
     * Set PaymentSystem as default (or not default), performing PATCH request to the server
     *
     * @memberof models.PaymentSystem#
     * @return {Promise} promise
     */
    _default: function (setDefault) {
      return $http.patch(this._url, setDefault !== false ? '"MAKE_DEFAULT"' : '"MAKE_NOT_DEFAULT"')
        .then(
          response => {
            this._weakCopy(response.data)
            return response
          }
        ).catch(
          rejection => {
            this.defaultPaymentSystem = setDefault === false
            return alertLiteSvc.alertError(rejection)
          }
        )
    },

    /**
     * Prepare paymentSystem
     *
     * @memberof models.PaymentSystem#
     * @override
     */
    _prepare: function () {
      if (this.paymentSystemType === 'AIRWALLEX' && !this.paymentMethods.length) {
        this.paymentMethods = [{ paymentMethod: 'CREDIT_CARD', transactionFeePercent: 0 }]
      }
      if (this.paymentSystemType === 'GOCARDLESS' || this.paymentSystemType === 'PAYPAL') {
        if (this.defaultTransactionFeePercent == null || this.defaultTransactionFeePercent === '') {
          this.defaultTransactionFeePercent = 0
        }
        if (typeof this.enableTransactionFee !== 'boolean') {
          this.enableTransactionFee = false
        }
        if (this.live == null) {
          this.live = false
        }
        if (this.autoPay == null) {
          this.autoPay = false
        }
      }
      if (this.paymentSystemType === 'PAYPAL') {
        const existing = this.webhookSettings && typeof this.webhookSettings === 'object' ? this.webhookSettings : {}
        this.webhookSettings = {
          enabled: false,
          ...existing,
          webhookId: existing.webhookId ?? ''
        }
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.PaymentSystem#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (this.paymentSystemType === 'AIRWALLEX') {
        this.defaultTransactionFeePercent = 0
        this.disableOtherPaymentMethods = true
        this.webhookSettings = { ...this.webhookSettings, enabled: window.ENV.APP_AIRWALLEX_WEBHOOK_SETTINGS_ENABLED }
      }

      if (this.paymentSystemType === 'GOCARDLESS' && this.webhookSettings) {
        var secret = this.webhookSettings.secret
        this.webhookSettings = {
          ...this.webhookSettings,
          enabled: secret != null && String(secret).length > 0
        }
      }

      if (this.paymentSystemType === 'PAYPAL' && this.webhookSettings) {
        var webhookId = this.webhookSettings.webhookId
        this.webhookSettings = {
          ...this.webhookSettings,
          enabled: webhookId != null && String(webhookId).length > 0
        }
      }

      return Entity.prototype._object.apply(this, arguments)
    }

  }, m2mMixin('currencies'), onOffMixin)

  /* === PaymentSystem static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(PaymentSystem, Entity)
  PaymentSystem._name = 'PaymentSystem'
  PaymentSystem._publicName = 'Payment System'
  PaymentSystem.init()

  // permissions
  PaymentSystem.access = {
    view: 'viewGlobalPayment',
    // create: 'createPayment',
    edit: 'editGlobalPayment'
    // delete: 'deletePayment'
  }

  // extra
  PaymentSystem.extra = {
    countries: [{
      id: Number,
      name: String,
      isoCode: String
    }],
    currencies: ['Currency']
  }

  return PaymentSystem
}
